/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSession } from "../../context/AuthContext";
import api from "../../services/api";
import { categoryEmojis } from "../../utils/constants";
import YouTube from "react-youtube";
import Dialog from "./Dialog";

interface Task {
  id: string;
  title: string;
  topic?: string;
  topicId?: string;
  status: string;
  points?: number;
  description?: string;
  type?: string;
  requirements?: string[];
  effort_minutes?: number;
  expected_outcome?: string;
  business_impact_category?: any;
}

interface CourseViewerProps {
  courseId: string;
  title: string;
  videoUrl: string;
  tasks: Task[];
  description?: string;
  highlightsLink?: string;
  category?: any;
  courseTopics?: string[];
  estimated_duration_minutes?: number;
  difficulty_level?: string;
  what_youll_learn?: string[];
  feedback_links?: string[];
  isCompleted?: boolean;
  createdAt?: string;
  coachName?: string;
  is_locked?: boolean;
  upsell_offer?: {
    name: string;
    price: number | null;
  };
}

const formatDateStr = (dateString: string | null | undefined) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString;
  return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};



const getStatusLabel = (status: string) => {
  if (!status || status.toLowerCase() === "not_started" || status.toLowerCase() === "assigned") return "▶️ Start Now";
  if (status.toLowerCase() === "started") return "🚀 Keep Going";
  const s = status.toLowerCase();
  if (s === "approved" || s === "completed" || s === "resolved") return "🟢 Verified ⭐⭐⭐⭐⭐";
  if (s === "rejected") return "🟣 Waiting for You →";
  if (s === "pending" || s === "submitted" || s === "support_open") return "▶️ Start Now";
  return "▶️ Start Now";
};

// Emojis mapping for categories


export default function CourseViewer({
  courseId,
  title,
  videoUrl,
  tasks: initialTasks,
  description,
  highlightsLink,
  category,
  courseTopics,
  estimated_duration_minutes,
  difficulty_level,
  what_youll_learn,
  feedback_links,
  isCompleted,
  createdAt,
  coachName,
  is_locked,
  upsell_offer
}: CourseViewerProps) {
  const navigate = useNavigate();
  const [activeVideoTab, setActiveVideoTab] = useState("FULL");
  const [isMounted, setIsMounted] = useState(false);
  const [completed, setCompleted] = useState(isCompleted);
  const [isCompleting, setIsCompleting] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [tasks, setTasks] = useState<Task[]>(initialTasks);
  const [showAddTask, setShowAddTask] = useState(false);
  const [topics, setTopics] = useState<any[]>([]);
  const [newTask, setNewTask] = useState({
    title: "",
    topicId: "",
    type: "TEXT" as "TEXT" | "LINK",
    description: "",
    points: 100,
    requirements: [] as string[]
  });
  const [currentReq, setCurrentReq] = useState("");
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [startSeconds, setStartSeconds] = useState(0);
  const [timeFetched, setTimeFetched] = useState(false);
  const [courseProgress, setCourseProgress] = useState<number>(0);
  
  const [isPlayingVideo, setIsPlayingVideo] = useState(false);
  const [isPlayingHighlights, setIsPlayingHighlights] = useState(false);

  const lastTrackedPercent = useRef(0);
  const playerRef = useRef<any>(null);

  const { data: session }: any = useSession();
  const userId = session?.user?.id;
  const userRole = session?.user?.role;
  const isCoach = userRole === "COACH" || userRole === "ADMIN";

  useEffect(() => {
    setIsMounted(true);
  }, [videoUrl]);

  useEffect(() => {
    setTasks(initialTasks);
  }, [initialTasks]);

  useEffect(() => {
    if (userId && !isCoach) {
      const fetchStatuses = () => {
        api.get(`/submissions/${userId}?t=${Date.now()}`)
          .then(res => {
            const data = res.data;
            if (Array.isArray(data)) {
              setTasks(prev => prev.map(t => {
                const sub = data.find((s: any) => s.id === t.id);
                return {
                  ...t,
                  status: sub ? sub.status : "pending"
                };
              }));
            }
          })
          .catch(err => console.error("Failed to fetch task statuses:", err));
      };

      fetchStatuses();
      const interval = setInterval(fetchStatuses, 10000); // Poll every 10s
      return () => clearInterval(interval);
    }
  }, [userId, isCoach, initialTasks]);

  useEffect(() => {
    if (userId && courseId && !isCoach) {
      api.get(`/events/last/${userId}/${courseId}`)
        .then(res => {
          const data = res.data;
          if (data.seconds) {
            console.log(`[Cinema Sentry] Native resume queued for ${data.seconds}s`);
            setStartSeconds(data.seconds);
            if (data.percent) {
              lastTrackedPercent.current = data.percent;
            }
          }
        })
        .catch(err => console.error("Failed to fetch resume time:", err))
        .finally(() => setTimeFetched(true));
    } else {
      setTimeFetched(true);
    }
  }, [userId, courseId, isCoach]);

  useEffect(() => {
    if (userId && courseId && !isCoach) {
      api.get(`/progress/${userId}`).then(res => {
        if (res.data && res.data[courseId] !== undefined) {
          setCourseProgress(res.data[courseId]);
        }
      }).catch(err => console.error("Failed to fetch progress:", err));
    }
  }, [userId, courseId, isCoach]);

  // Engagement tracking interval engine - Starts as soon as player is ready
  useEffect(() => {
    let interval: any;

    interval = setInterval(() => {
      const p = playerRef.current;
      if (p && p.getPlayerState) {
        const state = p.getPlayerState();
        // 1 = PLAYING, 3 = BUFFERING
        if (state === 1 || state === 3) {
          const currentTime = p.getCurrentTime();
          const duration = p.getDuration();
          if (duration > 0) {
            handleProgress({ played: currentTime / duration, playedSeconds: currentTime });
          }
        }
      }
    }, 5000); // Heartbeat every 5 seconds

    return () => clearInterval(interval);
  }, [userId, isCoach]); // Re-run if user context changes

  // Fetch topics for coach - Load early to ensure Edit dropdowns are populated
  useEffect(() => {
    if (isCoach) {
      api.get(`/topics`)
        .then(res => {
          const data = res.data;
          console.log(`[Cinema Sentry] Topics catalog loaded: ${data.length} items`);
          setTopics(data);
        })
        .catch(err => console.error("Failed to fetch topics:", err));
    }
  }, [isCoach]);

  const triggerAssignment = async () => {
    if (!hasStarted && userId && !isCoach) {
      setHasStarted(true);
      try {
        for (const task of tasks) {
          await api.post(`/submissions`, {
            taskId: task.id,
            userId: userId,
            status: "ASSIGNED"
          });
        }
      } catch (err) {
        console.error("Assignment sync failed:", err);
      }
    }
  };

  const handleProgress = async ({ played, playedSeconds }: { played: number; playedSeconds: number }) => {
    if (!userId || isCoach) return;
    const currentPercent = Math.round(played * 100);

    // Only track in 5% increments to save bandwidth while staying granular
    if (currentPercent >= lastTrackedPercent.current + 5) {
      lastTrackedPercent.current = currentPercent;
      try {
        await api.post(`/events/track`, {
          name: "CLASS_VIEWED",
          userId,
          properties: {
            courseId,
            percent: currentPercent,
            seconds: Math.round(playedSeconds)
          }
        });
      } catch (err) {
        console.warn("Progress tracking failed silently");
      }
    }
  };

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTask.title || !newTask.topicId) return;
    setIsCreating(true);
    try {
      const method = editingTaskId ? "PATCH" : "POST";
      const url = editingTaskId 
        ? `/tasks/${editingTaskId}` 
        : `/tasks`;

      console.log(`[TaskAction] ${method} to ${url}`, newTask);

      const res = await api.request({
        url,
        method,
        data: { ...newTask, courseId }
      });

      if (res.status === 200 || res.status === 201) {
        const { task } = res.data;
        console.log(`[TaskAction] Success response:`, task);
        if (editingTaskId) {
          setTasks(prev => prev.map(t => t.id === task.id ? {
            ...t,
            title: task.title,
            topic: task.topic?.name,
            topicId: task.topicId,
            points: task.points,
            description: task.description,
            type: task.type,
            requirements: task.requirements
          } : t));
        } else {
          setTasks(prev => [...prev, {
            id: task.id,
            title: task.title,
            topic: task.topic?.name,
            topicId: task.topicId,
            status: "pending",
            points: task.points,
            description: task.description,
            type: task.type,
            requirements: task.requirements
          }]);
        }
        closeModal();
      } else {
        console.error(`[TaskAction] Failed (${res.status}):`, res.data);
        alert(`Failed to save task: ${res.data || "Unknown server error"}`);
      }
    } catch (err: any) {
      console.error("[TaskAction] Exception:", err);
      alert(`Failed to save task: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsCreating(false);
    }
  };

  const closeModal = () => {
    setShowAddTask(false);
    setEditingTaskId(null);
    setNewTask({ title: "", topicId: "", type: "TEXT", description: "", points: 100, requirements: [] });
    setCurrentReq("");
  };

  // Safe YouTube ID extractor
  const getYouTubeId = (url: string) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
    return match ? match[1] : null;
  };

  const videoId = getYouTubeId(videoUrl);
  const highlightsVideoId = highlightsLink ? getYouTubeId(highlightsLink) : null;
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isHighlightsVideoLoaded, setIsHighlightsVideoLoaded] = useState(false);
  const onPlayerReady = (event: any) => { 
    playerRef.current = event.target; 
    setIsVideoLoaded(true);
  };
  const onPlayerStateChange = (event: any) => { 
    if (event.data === 1) triggerAssignment(); 
    if (event.data === 0 && feedback_links && feedback_links.length > 0) setShowFeedbackModal(true);
  };

  const renderCompleteButton = () => {
    if (is_locked) return null;
    return (
    <div className="w-full mt-8 pt-8 border-t border-outline-variant/10 flex justify-center">
      {completed ? (
        <div className="w-full max-w-xl mx-auto bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-fuchsia-500/10 border border-emerald-500/20 p-6 sm:p-8 rounded-[2rem] text-center shadow-2xl relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-400/10 rounded-full blur-3xl -mr-10 -mt-10 animate-pulse" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-fuchsia-400/10 rounded-full blur-3xl -ml-10 -mb-10 animate-pulse" />
          
          <div className="relative z-10 space-y-4">
            <h3 className="text-2xl font-black text-emerald-500 flex items-center justify-center gap-2 mb-2">
              <span className="text-3xl">🎉</span> Mission Complete!
            </h3>
            
            <div className="inline-flex items-center gap-2 bg-white/60 dark:bg-black/40 px-4 py-2 rounded-xl border border-emerald-500/10 shadow-sm mb-6">
              <span className="text-xl">🏆</span>
              <span className="font-bold text-on-surface">+50 XP Earned</span>
            </div>
            
            <Link 
              to="/dashboard"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-white dark:bg-surface-container-high text-on-surface hover:bg-emerald-50 dark:hover:bg-emerald-950/30 px-8 py-4 rounded-xl font-black text-[11px] uppercase tracking-[0.15em] border border-outline-variant/20 shadow-xl transition-all hover:-translate-y-1 hover:shadow-2xl hover:border-emerald-500/30"
            >
              <span className="text-lg">🔓</span>
              Next Mission Unlocked
            </Link>
          </div>
        </div>
      ) : (
        <button 
          onClick={() => setShowCompleteConfirm(true)}
          className="w-full sm:w-auto group relative flex items-center justify-center gap-3 bg-gradient-to-r from-primary to-primary-variant hover:from-primary-variant hover:to-primary text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-primary/20 hover:shadow-2xl hover:shadow-primary/30 transition-all duration-300 transform hover:-translate-y-0.5 overflow-hidden"
        >
          <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
          <span className="material-symbols-outlined text-[18px] relative z-10 group-hover:scale-110 transition-transform duration-300">task_alt</span>
          <span className="relative z-10">Mark as Completed</span>
        </button>
      )}
    </div>
    );
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {isCoach ? (
        <>
{/* Course Header */}
      <div className="flex flex-col gap-8 lg:gap-6 lg:flex-row lg:items-start lg:justify-between mb-10 mt-2">
        <div className="flex flex-col sm:flex-row justify-between items-start gap-8 w-full">
          <div className="flex-1 min-w-0">
            {/* Breadcrumb */}
            <nav className="flex flex-wrap items-center gap-2 mb-4">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-primary transition-colors group"
              >
                <span className="material-symbols-outlined text-[14px] transition-transform group-hover:-translate-x-1">arrow_back</span>
                Back
              </button>
              <span className="text-slate-300 text-[10px]">/</span>
              <Link to="/dashboard" className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-primary transition-colors">Academy</Link>
              <span className="text-slate-300 text-[10px]">/</span>
              <Link to="/courses" className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-primary transition-colors">Courses</Link>
              <span className="text-slate-300 text-[10px]">/</span>
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">Module Focus</span>
            </nav>
            <h2 className="text-3xl font-black text-on-surface tracking-tighter leading-none mb-4">{title}</h2>
            <div className="flex flex-wrap items-center gap-3 mb-2">

              {category && (
                <button 
                  onClick={() => navigate(`/courses?category=${encodeURIComponent(category.name)}`)}
                  className="flex items-center gap-1.5 px-3 py-1 bg-emerald-500/10 text-emerald-700 border border-emerald-500/30 rounded-lg text-[10px] font-black uppercase tracking-widest shadow-sm cursor-pointer hover:bg-emerald-500/20 transition-colors"
                >
                  <span className="material-symbols-outlined text-[14px]">folder</span>
                  {category.name}
                </button>
              )}
              {courseTopics && courseTopics.map((topic, i) => (
                <button 
                  key={i} 
                  onClick={() => navigate(`/courses?topic=${encodeURIComponent(topic)}`)}
                  className="flex items-center gap-1 text-[10px] font-bold text-slate-600 bg-slate-200/50 px-2.5 py-1 rounded-lg uppercase tracking-widest border border-slate-300/30 cursor-pointer hover:bg-slate-300/50 transition-colors"
                >
                  {topic}
                </button>
              ))}
              {difficulty_level && (
                <div className={`flex items-center gap-1.5 text-[10px] font-black px-3 py-1 rounded-lg uppercase shadow-sm text-white tracking-widest ${
                  difficulty_level === 'beginner' ? 'bg-emerald-500' :
                  difficulty_level === 'intermediate' ? 'bg-amber-500' :
                  difficulty_level === 'advanced' ? 'bg-red-500' :
                  'bg-slate-500'
                }`}>
                  <span className="text-[12px]">
                    {difficulty_level === 'beginner' && '🟢'}
                    {difficulty_level === 'intermediate' && '🟡'}
                    {difficulty_level === 'advanced' && '🔴'}
                  </span>
                  <span>{difficulty_level}</span>
                </div>
              )}
              {estimated_duration_minutes && (
                <div className="flex items-center gap-1.5 text-[10px] font-black text-white bg-indigo-500 px-3 py-1 rounded-lg uppercase shadow-sm tracking-widest">
                  <span className="material-symbols-outlined text-[14px]">schedule</span>
                  <span>{Math.floor(estimated_duration_minutes / 60) > 0 ? `${Math.floor(estimated_duration_minutes / 60)}H ` : ''}{estimated_duration_minutes % 60}M</span>
                </div>
              )}
              {createdAt && (
                <div className="flex items-center gap-1.5 text-[10px] font-black text-slate-500 bg-slate-100 px-3 py-1 rounded-lg uppercase shadow-sm tracking-widest border border-slate-200">
                  <span className="material-symbols-outlined text-[14px]">calendar_month</span>
                  <span>{formatDateStr(createdAt)}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* COMPLETION WIDGET */}
          {!isCoach && (
            <div className="w-full sm:w-72 flex-shrink-0 bg-white p-6 rounded-[2rem] border border-outline-variant/10 shadow-sm self-stretch sm:self-center">
              <div className="flex justify-between items-center text-[11px] font-black uppercase tracking-widest mb-4">
                <span className={completed || courseProgress === 100 ? "text-slate-400" : courseProgress === 0 ? "text-primary" : "text-amber-500"}>
                  {completed || courseProgress === 100 ? "Mission Completed" : courseProgress === 0 ? "Start Mission" : "🔥 Next Mission"}
                </span>
                <span className={completed || courseProgress === 100 ? "text-emerald-500" : "text-slate-500"}>{completed || courseProgress === 100 ? "Verified" : `${courseProgress}%`}</span>
              </div>
              <div className="h-2.5 w-full bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full transition-all duration-1000 ${completed || courseProgress === 100 ? "bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.3)]" : "bg-[#066444]"}`} style={{ width: `${completed ? 100 : courseProgress}%` }} />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Primary Viewport (Left) */}
        <div className="lg:col-span-8 space-y-6">
          <div className="space-y-3">
            {highlightsLink && (
              <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 pl-4">Full Class Session</h4>
            )}
            <div id="tour-course-content" className="aspect-video bg-black rounded-[2.5rem] overflow-hidden shadow-2xl border border-white/5 relative group z-0">
              {is_locked ? (
                <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-slate-900/90 backdrop-blur-xl text-white p-6 text-center overflow-hidden">
                  <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at center, #3b82f6 0%, transparent 70%)' }}></div>
                  <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mb-6 border border-white/20 relative z-10 shadow-2xl">
                    <span className="material-symbols-outlined text-4xl">lock</span>
                  </div>
                  <h3 className="text-2xl font-black tracking-tight mb-2 relative z-10">Premium Content</h3>
                  <p className="text-sm font-medium text-white/70 mb-8 max-w-md relative z-10">This course is locked. Upgrade your membership to instantly unlock full access to this video and its resources.</p>
                  
                  {upsell_offer ? (
                    <div className="bg-white text-slate-900 p-6 rounded-2xl w-full max-w-sm shadow-2xl border border-white/20 relative z-10 transform hover:scale-105 transition-transform duration-300">
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-600 mb-2">Required Plan</p>
                      <h4 className="text-xl font-black mb-6">{upsell_offer.name}</h4>
                      <button onClick={() => {
                        const phone = (import.meta.env.VITE_SUPPORT_WHATSAPP || '').replace(/[^0-9]/g, '');
                        const text = encodeURIComponent(`Hi HitClicks Team,\n\nI am interested on ${upsell_offer.name} membership. Please provide me with the details to upgrade.\n\nThank you.`);
                        window.open(`https://wa.me/${phone}?text=${text}`, '_blank');
                      }} className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl transition-colors shadow-lg shadow-emerald-500/30 flex items-center justify-center gap-2">
                        <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.888-.788-1.489-1.761-1.663-2.06-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>
                        Contact via WhatsApp
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => {
                      const email = import.meta.env.VITE_SUPPORT_EMAIL || 'contact@hitclicks.com';
                      const subject = encodeURIComponent('Membership Upgrade Request');
                      const body = encodeURIComponent('Hi HitClicks Team,\n\nI am interested in upgrading my membership. Please provide me with the upgrade options.\n\nThank you.');
                      window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
                    }} className="px-8 py-3.5 bg-white text-slate-900 font-bold rounded-xl transition-colors shadow-xl hover:bg-slate-100 relative z-10 flex items-center gap-2 mx-auto">
                      <span className="material-symbols-outlined text-sm">mail</span>
                      Contact Support
                    </button>
                  )}
                </div>
              ) : isMounted && videoId && timeFetched ? (
                <>
                  {!isPlayingVideo ? (
                    <div className="w-full h-full relative cursor-pointer group flex items-center justify-center" onClick={() => setIsPlayingVideo(true)}>
                      <img 
                        src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`} 
                        alt="Video Thumbnail"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        onError={(e: any) => { e.target.onerror = null; e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`; }}
                      />
                      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-300" />
                      <div className="relative z-10 w-20 h-20 bg-primary text-white rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(255,0,0,0.5)] transform transition-all duration-300 group-hover:scale-110 group-hover:bg-primary-variant">
                        <span className="material-symbols-outlined text-4xl ml-2">play_arrow</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      {!isVideoLoaded && (
                        <div className="absolute inset-0 z-10 w-full h-full flex flex-col items-center justify-center bg-black/80 backdrop-blur-md text-white gap-4">
                          <span className="material-symbols-outlined text-6xl opacity-60 animate-spin text-white">progress_activity</span>
                          <p className="text-xs font-black uppercase tracking-[0.2em] opacity-80 text-white">Buffering Stream...</p>
                        </div>
                      )}
                      <div className={`w-full h-full [&>div]:w-full [&>div]:h-full [&_iframe]:w-full [&_iframe]:h-full [&_iframe]:pointer-events-auto transition-opacity duration-1000 ${isVideoLoaded ? 'opacity-100' : 'opacity-0'}`}>
                        <YouTube
                          videoId={videoId || undefined}
                          opts={{
                            width: '100%',
                            height: '100%',
                            playerVars: {
                              autoplay: 1,
                              modestbranding: 1,
                              rel: 0,
                              iv_load_policy: 3,
                              start: startSeconds,
                              playsinline: 1
                            }
                          }}
                          onReady={onPlayerReady}
                          onStateChange={onPlayerStateChange}
                        />
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center bg-surface-container-highest/20 text-on-surface-variant gap-4">
                  <span className="material-symbols-outlined text-6xl opacity-20 animate-pulse">video_library</span>
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-40">Preparing Interface...</p>
                </div>
              )}
            </div>
          </div>

          {/* Conditional Layout for Left Column */}
          {isCoach && !is_locked ? (
            /* COACH: Task List below video */
            <div id="tour-course-actions" className="py-10">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-lg font-black text-on-surface tracking-tight leading-tight">Course Tasks</h3>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">{tasks.length} Tasks to Do</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {tasks.length > 0 ? tasks.map((task) => (
                  <div
                    key={task.id}
                    onClick={() => navigate(`/tasks/${task.id}`)}
                    className="bg-surface-container-lowest p-6 rounded-2xl border border-outline-variant/10 hover:border-primary/30 transition-all group cursor-pointer shadow-sm active:scale-[0.98] flex items-center justify-between"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-8 h-8 rounded-xl border flex items-center justify-center transition-all ${task.status?.toLowerCase() === "approved"
                          ? "bg-emerald-500 border-emerald-500 text-white shadow-lg shadow-emerald-500/20"
                          : "bg-white border-outline-variant/10 group-hover:border-primary group-hover:scale-105"
                        }`}>
                        <span className="material-symbols-outlined text-[18px] font-black">
                          {task.status?.toLowerCase() === "approved" ? "check" : "bolt"}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-black text-on-surface group-hover:text-primary transition-colors tracking-tight">{task.title}</p>
                        <span className="text-[9px] font-black uppercase tracking-[0.15em] text-slate-400">{task.topic || "General"}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {/* Coach task management moved to CourseTaskManager */}
                    </div>
                  </div>
                )) : (
                  <div className="col-span-full py-12 text-center opacity-30">
                    <span className="material-symbols-outlined text-4xl mb-2">assignment</span>
                    <p className="text-[10px] font-black uppercase tracking-widest">No tasks assigned to this course yet</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* STUDENT: Description below video */
            <div className="bg-surface-container-low p-8 rounded-[2.5rem] border border-outline-variant/10">
              <h3 className="text-xl font-black text-on-surface mb-3 tracking-tight">About This Course</h3>
              <p className="text-sm text-slate-500 leading-relaxed font-medium whitespace-pre-line">
                {description || "No description available."}
              </p>
              
              {what_youll_learn && what_youll_learn.length > 0 && (
                <div className="mt-8 pt-8 border-t border-outline-variant/10">
                  <h4 className="text-[11px] font-black uppercase tracking-[0.2em] text-primary/60 mb-4">What You'll Learn</h4>
                  <ul className="space-y-3">
                    {what_youll_learn.map((item, idx) => (
                      <li key={idx} className="flex gap-3 items-start">
                        <span className="material-symbols-outlined text-primary text-[18px] shrink-0 mt-0.5">check_circle</span>
                        <span className="text-sm font-medium text-slate-600 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}



              {renderCompleteButton()}
            </div>
          )}
        </div>
        <div className="lg:col-span-4 space-y-6">
          {/* Highlights Video Player (on the right) */}
          {highlightsLink && (
            <div className="space-y-3">
              <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-primary pl-4">Class Highlights</h4>
              <div className="aspect-video bg-black rounded-[2.5rem] overflow-hidden shadow-2xl border border-white/5 relative group z-0">
                {isMounted && highlightsVideoId ? (
                  <>
                    {!isPlayingHighlights ? (
                      <div className="w-full h-full relative cursor-pointer group flex items-center justify-center" onClick={() => setIsPlayingHighlights(true)}>
                        <img 
                          src={`https://img.youtube.com/vi/${highlightsVideoId}/maxresdefault.jpg`} 
                          alt="Highlights Thumbnail"
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          onError={(e: any) => { e.target.onerror = null; e.target.src = `https://img.youtube.com/vi/${highlightsVideoId}/hqdefault.jpg`; }}
                        />
                        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-300" />
                        <div className="relative z-10 w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(255,0,0,0.5)] transform transition-all duration-300 group-hover:scale-110 group-hover:bg-primary-variant">
                          <span className="material-symbols-outlined text-3xl ml-1">play_arrow</span>
                        </div>
                      </div>
                    ) : (
                      <>
                        {!isHighlightsVideoLoaded && (
                          <div className="absolute inset-0 z-10 w-full h-full flex flex-col items-center justify-center bg-black/80 backdrop-blur-md text-white gap-4">
                            <span className="material-symbols-outlined text-6xl opacity-60 animate-spin text-white">progress_activity</span>
                            <p className="text-xs font-black uppercase tracking-[0.2em] opacity-80 text-white">Buffering Highlights...</p>
                          </div>
                        )}
                        <div className={`w-full h-full [&>div]:w-full [&>div]:h-full [&_iframe]:w-full [&_iframe]:h-full [&_iframe]:pointer-events-auto transition-opacity duration-1000 ${isHighlightsVideoLoaded ? 'opacity-100' : 'opacity-0'}`}>
                          <YouTube
                            videoId={highlightsVideoId || undefined}
                            opts={{
                              width: '100%',
                              height: '100%',
                              playerVars: {
                                autoplay: 1,
                                modestbranding: 1,
                                rel: 0,
                                iv_load_policy: 3,
                                playsinline: 1
                              }
                            }}
                            onReady={() => setIsHighlightsVideoLoaded(true)}
                          />
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-surface-container-highest/20 text-on-surface-variant gap-4">
                    <span className="material-symbols-outlined text-6xl opacity-20 animate-pulse">video_library</span>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-40">Preparing Highlights...</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Conditional Layout for Right Column */}
          {isCoach ? (
            /* COACH: Description in sidebar */
            <div className="bg-surface-container-lowest p-8 rounded-[2.5rem] border border-outline-variant/10 shadow-xl sticky top-24">
              <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-primary/60 mb-6">About This Course</h3>
              <p className="text-sm text-slate-500 leading-relaxed font-medium whitespace-pre-line">
                {description || "No description available."}
              </p>

              {what_youll_learn && what_youll_learn.length > 0 && (
                <div className="mt-6 pt-6 border-t border-outline-variant/10">
                  <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-primary/60 mb-4">What You'll Learn</h4>
                  <ul className="space-y-3">
                    {what_youll_learn.map((item, idx) => (
                      <li key={idx} className="flex gap-2 items-start">
                        <span className="material-symbols-outlined text-primary text-[16px] shrink-0 mt-0.5">check_circle</span>
                        <span className="text-xs font-medium text-slate-600 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}



              <div className="mt-8 pt-8 border-t border-outline-variant/10 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Course Duration</span>
                  <span className="text-xs font-black text-on-surface">{estimated_duration_minutes ? `${estimated_duration_minutes} Minutes` : 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Assigned Coach</span>
                  <span className="text-xs font-black text-on-surface">{coachName || 'Pending Assignment'}</span>
                </div>
              </div>
              {renderCompleteButton()}
            </div>
          ) : is_locked ? null : (
            /* STUDENT: Tasks in sidebar */
            <div id="tour-course-actions" className="bg-surface-container-low p-8 rounded-[2.5rem] border border-outline-variant/10 shadow-xl sticky top-24">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-xs font-black uppercase tracking-widest text-primary flex items-center gap-2">
                    🚀 YOUR NEXT BEST ACTIONS
                  </h3>
                  <p className="text-[11px] font-bold text-slate-500 mt-1">{tasks.length} actions to unlock 🚀 Momentum</p>
                </div>
              </div>

              <div className="space-y-6">
                {tasks.length > 0 ? tasks.map((task) => {
                  const categoryName = task.business_impact_category?.name || 'General';
                  const categoryEmoji = categoryEmojis[categoryName] || '📋';
                  
                  return (
                    <div
                      key={task.id}
                      onClick={() => navigate(`/tasks/${task.id}`)}
                      className="bg-surface-container-lowest border border-outline-variant/10 p-6 rounded-[2rem] shadow-sm hover:border-primary/30 transition-all cursor-pointer group active:scale-[0.98]"
                    >
                      <h4 className="text-xl font-black text-on-surface mb-3 leading-tight group-hover:text-primary transition-colors tracking-tight">
                        {task.title}
                      </h4>

                      <div className="flex flex-wrap items-center gap-3 mb-5">
                        <span className="text-xs font-bold text-on-surface-variant flex items-center gap-1 bg-surface-container-high px-2 py-1 rounded-md">
                          {categoryEmoji} {categoryName}
                        </span>
                        <span className="text-xs font-bold text-on-surface-variant flex items-center gap-1 bg-surface-container-high px-2 py-1 rounded-md">
                          ⏱ {task.effort_minutes ?? 15} min
                        </span>
                        <span className="text-xs font-black text-green-600 dark:text-green-400 flex items-center gap-1 bg-green-500/10 px-2 py-1 rounded-md">
                          +{task.points ?? 100} XP
                        </span>
                      </div>

                      <p className="text-sm text-slate-500 font-medium leading-relaxed mb-5">
                        {task.expected_outcome || "Complete this task to unlock your next business milestone."}
                      </p>

                      <div>
                        <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold shadow-md transition-all justify-center ${
                          (!task.status || task.status.toLowerCase() === 'pending' || task.status.toLowerCase() === 'assigned') 
                            ? 'bg-[#066444] text-white hover:bg-[#066444]/90'
                            : 'bg-surface-container-high text-on-surface-variant hover:bg-surface-container-high/80'
                        }`}>
                          {getStatusLabel(task.status)}
                        </span>
                      </div>
                    </div>
                  );
                }) : (
                  <p className="text-center py-10 text-xs italic text-slate-400">No tasks assigned to this course yet.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
        </>
      ) : (

      
      <div className="max-w-4xl mx-auto space-y-6 pb-20 px-4 sm:px-6 md:px-8">
        
        {/* 1. Breadcrumbs */}
        <nav className="flex flex-wrap items-center gap-2 mt-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-primary transition-colors group"
          >
            <span className="material-symbols-outlined text-[14px] transition-transform group-hover:-translate-x-1">arrow_back</span>
            Back
          </button>
          <span className="text-slate-300 text-[10px]">/</span>
          <Link to="/dashboard" className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-primary transition-colors">Academy</Link>
          <span className="text-slate-300 text-[10px]">/</span>
          <Link to="/courses" className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-primary transition-colors">Courses</Link>
          <span className="text-slate-300 text-[10px]">/</span>
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">Module Focus</span>
        </nav>

        {/* 2. Video Tabs & Player */}
        <div className="space-y-4">
          {highlightsLink && (
            <div className="inline-flex bg-slate-200 dark:bg-slate-800/80 p-1 rounded-xl mb-2">
                <button
                  onClick={() => setActiveVideoTab("FULL")}
                  className={`whitespace-nowrap px-6 py-2 rounded-lg text-[10px] font-black uppercase tracking-[0.2em] transition-all flex items-center gap-2 ${activeVideoTab === 'FULL' ? 'bg-white dark:bg-slate-700 shadow-sm text-primary' : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'}`}
                >
                  <span className="material-symbols-outlined text-[14px]">play_circle</span>
                  Full Class
                </button>
                <button
                  onClick={() => setActiveVideoTab("HIGHLIGHTS")}
                  className={`whitespace-nowrap px-6 py-2 rounded-lg text-[10px] font-black uppercase tracking-[0.2em] transition-all flex items-center gap-2 ${activeVideoTab === 'HIGHLIGHTS' ? 'bg-white dark:bg-slate-700 shadow-sm text-primary' : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'}`}
                >
                  <span className="material-symbols-outlined text-[14px]">bolt</span>
                  Highlights
                </button>
              </div>
          )}
          
          {(!highlightsLink || activeVideoTab === "FULL") ? (
            <div id="tour-course-content" className="aspect-video bg-black rounded-[2.5rem] overflow-hidden shadow-2xl border border-white/5 relative group z-0">
              {is_locked ? (
                <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-slate-900/90 backdrop-blur-xl text-white p-6 text-center overflow-hidden">
                  <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at center, #3b82f6 0%, transparent 70%)' }}></div>
                  <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mb-6 border border-white/20 relative z-10 shadow-2xl">
                    <span className="material-symbols-outlined text-4xl">lock</span>
                  </div>
                  <h3 className="text-2xl font-black tracking-tight mb-2 relative z-10">Premium Content</h3>
                  <p className="text-slate-300 font-medium max-w-sm mb-8 relative z-10">
                    {upsell_offer?.name 
                      ? `Upgrade to the ${upsell_offer.name} to unlock this masterclass and its resources.`
                      : 'This content requires an active premium subscription to access.'}
                  </p>
                  <button
                    onClick={() => window.open(upsell_offer ? `https://billing.stripe.com/p/login/test` : 'https://billing.stripe.com', '_blank')}
                    className="bg-white text-slate-900 px-8 py-4 rounded-xl font-black uppercase tracking-widest text-xs hover:scale-105 transition-transform shadow-xl relative z-10 flex items-center gap-2"
                  >
                    <span className="material-symbols-outlined text-[16px]">workspace_premium</span>
                    {upsell_offer?.price ? `Unlock for $${upsell_offer.price}/mo` : 'Upgrade Plan'}
                  </button>
                </div>
              ) : null}

              {!isMounted ? (
                <div className="w-full h-full flex flex-col items-center justify-center bg-surface-container-highest/20 text-on-surface-variant gap-4">
                  <span className="material-symbols-outlined text-6xl opacity-20 animate-pulse">video_library</span>
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-40">Loading Player...</p>
                </div>
              ) : (
                <div className={`w-full h-full [&>div]:w-full [&>div]:h-full [&_iframe]:w-full [&_iframe]:h-full [&_iframe]:pointer-events-auto transition-opacity duration-1000 opacity-100`}>
                  <>
                    {!isPlayingVideo ? (
                      <div className="w-full h-full relative cursor-pointer group flex items-center justify-center" onClick={() => setIsPlayingVideo(true)}>
                        <img 
                          src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`} 
                          alt="Video Thumbnail"
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          onError={(e: any) => { e.target.onerror = null; e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`; }}
                        />
                        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-300" />
                        <div className="relative z-10 w-20 h-20 bg-primary text-white rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(255,0,0,0.5)] transform transition-all duration-300 group-hover:scale-110 group-hover:bg-primary-variant">
                          <span className="material-symbols-outlined text-4xl ml-2">play_arrow</span>
                        </div>
                      </div>
                    ) : (
                      <>
                        {!isVideoLoaded && (
                          <div className="absolute inset-0 z-10 w-full h-full flex flex-col items-center justify-center bg-black/80 backdrop-blur-md text-white gap-4">
                            <span className="material-symbols-outlined text-6xl opacity-60 animate-spin text-white">progress_activity</span>
                            <p className="text-xs font-black uppercase tracking-[0.2em] opacity-80 text-white">Buffering Stream...</p>
                          </div>
                        )}
                        <div className={`w-full h-full [&>div]:w-full [&>div]:h-full [&_iframe]:w-full [&_iframe]:h-full [&_iframe]:pointer-events-auto transition-opacity duration-1000 ${isVideoLoaded ? 'opacity-100' : 'opacity-0'}`}>
                          <YouTube
                            videoId={videoId || undefined}
                            opts={{
                              width: '100%',
                              height: '100%',
                              playerVars: {
                                autoplay: 1,
                                modestbranding: 1,
                                rel: 0,
                                iv_load_policy: 3,
                                start: startSeconds,
                                playsinline: 1
                              }
                            }}
                            onReady={onPlayerReady}
                            onStateChange={onPlayerStateChange}
                          />
                        </div>
                      </>
                    )}
                  </>
                </div>
              )}
            </div>
          ) : (
            <div className="aspect-video bg-black rounded-[2.5rem] overflow-hidden shadow-2xl border border-white/5 relative group z-0">
              {isMounted && highlightsVideoId ? (
                <>
                  {!isPlayingHighlights ? (
                    <div className="w-full h-full relative cursor-pointer group flex items-center justify-center" onClick={() => setIsPlayingHighlights(true)}>
                      <img 
                        src={`https://img.youtube.com/vi/${highlightsVideoId}/maxresdefault.jpg`} 
                        alt="Highlights Thumbnail"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        onError={(e: any) => { e.target.onerror = null; e.target.src = `https://img.youtube.com/vi/${highlightsVideoId}/hqdefault.jpg`; }}
                      />
                      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-300" />
                      <div className="relative z-10 w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(255,0,0,0.5)] transform transition-all duration-300 group-hover:scale-110 group-hover:bg-primary-variant">
                        <span className="material-symbols-outlined text-3xl ml-1">play_arrow</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      {!isHighlightsVideoLoaded && (
                        <div className="absolute inset-0 z-10 w-full h-full flex flex-col items-center justify-center bg-black/80 backdrop-blur-md text-white gap-4">
                          <span className="material-symbols-outlined text-6xl opacity-60 animate-spin text-white">progress_activity</span>
                          <p className="text-xs font-black uppercase tracking-[0.2em] opacity-80 text-white">Buffering Highlights...</p>
                        </div>
                      )}
                      <div className={`w-full h-full [&>div]:w-full [&>div]:h-full [&_iframe]:w-full [&_iframe]:h-full [&_iframe]:pointer-events-auto transition-opacity duration-1000 ${isHighlightsVideoLoaded ? 'opacity-100' : 'opacity-0'}`}>
                        <YouTube
                          videoId={highlightsVideoId || undefined}
                          opts={{
                            width: '100%',
                            height: '100%',
                            playerVars: {
                              autoplay: 1,
                              modestbranding: 1,
                              rel: 0,
                              iv_load_policy: 3,
                              playsinline: 1
                            }
                          }}
                          onReady={() => setIsHighlightsVideoLoaded(true)}
                        />
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center bg-surface-container-highest/20 text-on-surface-variant gap-4">
                  <span className="material-symbols-outlined text-6xl opacity-20 animate-pulse">video_library</span>
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-40">Preparing Highlights...</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 3. Title */}
        <h2 className="text-xl sm:text-2xl md:text-3xl font-black text-on-surface tracking-tight leading-tight pt-2">{title}</h2>

        {/* 4. Tags */}
        <div className="flex flex-col gap-2 w-full">
          <div className="flex flex-wrap items-center gap-2">
            {difficulty_level && (
              <div className={`flex items-center gap-1.5 text-[10px] font-black px-3 py-1 rounded-lg uppercase shadow-sm text-white tracking-widest whitespace-nowrap shrink-0 ${
                difficulty_level === 'beginner' ? 'bg-emerald-500' :
                difficulty_level === 'intermediate' ? 'bg-amber-500' :
                difficulty_level === 'advanced' ? 'bg-red-500' :
                'bg-slate-500'
              }`}>
                <span className="material-symbols-outlined text-[14px]">lens</span>
                <span>{difficulty_level}</span>
              </div>
            )}
            {estimated_duration_minutes && (
              <div className="flex items-center gap-1.5 text-[10px] font-black text-white bg-indigo-500 px-3 py-1 rounded-lg uppercase shadow-sm tracking-widest whitespace-nowrap shrink-0">
                <span className="material-symbols-outlined text-[14px]">schedule</span>
                <span>{Math.floor(estimated_duration_minutes / 60) > 0 ? `${Math.floor(estimated_duration_minutes / 60)}H ` : ''}{estimated_duration_minutes % 60}M</span>
              </div>
            )}
            {category && (
              <button 
                onClick={() => navigate(`/courses?category=${encodeURIComponent(category.name)}`)}
                className="flex items-center gap-1.5 px-3 py-1 bg-emerald-500/10 text-emerald-700 border border-emerald-500/30 rounded-lg text-[10px] font-black uppercase tracking-widest shadow-sm cursor-pointer hover:bg-emerald-500/20 transition-colors whitespace-nowrap shrink-0"
              >
                <span className="material-symbols-outlined text-[14px]">folder</span>
                {category.name}
              </button>
            )}
          </div>
          {courseTopics && courseTopics.length > 0 && (
            <div className="flex overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] flex-nowrap whitespace-nowrap items-center gap-2 pb-1 w-full max-w-full">
              {courseTopics.map((topic, i) => (
                <button 
                  key={i} 
                  onClick={() => navigate(`/courses?topic=${encodeURIComponent(topic)}`)}
                  className="flex items-center gap-1 text-[10px] font-bold text-slate-600 bg-slate-200/50 px-2.5 py-1 rounded-lg uppercase tracking-widest border border-slate-300/30 cursor-pointer hover:bg-slate-300/50 transition-colors whitespace-nowrap shrink-0"
                >
                  {topic}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 5. Buttons Side-by-Side */}
        <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-outline-variant/10">
          <button 
            onClick={() => setShowCompleteConfirm(true)}
            disabled={completed}
            className={`flex-1 group relative flex items-center justify-center gap-3 px-6 py-4 rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl transition-all duration-300 transform overflow-hidden ${
              completed ? 'bg-emerald-500/20 text-emerald-600 shadow-none cursor-default' : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-600/20 hover:shadow-2xl hover:shadow-emerald-600/30 hover:-translate-y-0.5'
            }`}
          >
            {completed ? (
               <>
                 <span className="material-symbols-outlined text-[18px]">verified</span>
                 <span>Completed</span>
               </>
            ) : (
               <>
                 <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
                 <span className="material-symbols-outlined text-[18px] relative z-10 group-hover:scale-110 transition-transform duration-300">task_alt</span>
                 <span className="relative z-10">Mark as Completed</span>
               </>
            )}
          </button>

          {tasks.length > 0 && (
            <button 
              onClick={() => navigate(`/tasks/${tasks[0].id}`)}
              className="flex-1 group relative flex items-center justify-center gap-3 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-4 rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-indigo-600/20 hover:shadow-2xl hover:shadow-indigo-600/30 transition-all duration-300 transform hover:-translate-y-0.5 overflow-hidden"
            >
              <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
              <span className="material-symbols-outlined text-[18px] relative z-10 group-hover:scale-110 transition-transform duration-300">rocket_launch</span>
              <span className="relative z-10">Start Task Now</span>
            </button>
          )}
        </div>

        {/* 6. What you'll learn */}
        {what_youll_learn && what_youll_learn.length > 0 && (
          <div className="bg-surface-container-low p-6 sm:p-8 rounded-[2.5rem] border border-outline-variant/10">
            <h4 className="text-[11px] font-black uppercase tracking-[0.2em] text-primary/60 mb-6">What You'll Learn</h4>
            <ul className="space-y-4">
              {what_youll_learn.map((item, idx) => (
                <li key={idx} className="flex gap-3 items-start">
                  <span className="material-symbols-outlined text-primary text-[20px] shrink-0 mt-0.5">check_circle</span>
                  <span className="text-sm font-bold text-slate-600 leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 7. Task Details */}
        {tasks.length > 0 && (
          <div className="space-y-4 pt-4">
            <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 flex items-center gap-2 mb-4">
              ?? Tasks & Actions
            </h3>
            {tasks.map((task) => (
              <div key={task.id} className="bg-surface-container-low p-6 rounded-[2rem] border border-outline-variant/10 shadow-sm flex flex-col gap-5 hover:border-primary/30 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <h4 className="text-lg sm:text-xl font-black text-on-surface leading-tight">
                    {task.title}
                  </h4>
                  <span className="text-xs font-black text-green-600 dark:text-green-400 flex items-center gap-1 bg-green-500/10 px-3 py-1.5 rounded-lg shrink-0">
                    +{task.points ?? 100} XP
                  </span>
                </div>
                <button 
                  onClick={() => navigate(`/tasks/${task.id}`)}
                  className="w-full flex items-center justify-center gap-2 bg-surface-container-high hover:bg-primary text-slate-600 hover:text-white px-6 py-4 rounded-xl font-black text-xs uppercase tracking-[0.2em] transition-all duration-300"
                >
                  <span className="material-symbols-outlined text-[16px]">play_arrow</span>
                  Start Task Now
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      )}

      {showAddTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-on-surface/60 backdrop-blur-sm" onClick={closeModal} />
          <div className="relative bg-surface-container-lowest w-full max-w-lg rounded-[2.5rem] border border-outline-variant/20 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 max-h-[90vh] flex flex-col">
            <div className="p-8 pb-6 border-b border-outline-variant/10">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-black tracking-tight">{editingTaskId ? "Edit Task" : "Add New Task"}</h3>
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mt-1">
                    {editingTaskId ? "Change the details of this task" : `Add a new task to ${title}`}
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  className="w-10 h-10 rounded-full bg-surface-container-high flex items-center justify-center hover:bg-error-container/20 hover:text-error transition-all"
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
            </div>

            <div className="p-8 pt-6 overflow-y-auto custom-scrollbar flex-1">
              <form onSubmit={handleAddTask} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-4">Task Title *</label>
                  <input
                    required
                    value={newTask.title}
                    onChange={e => setNewTask({ ...newTask, title: e.target.value })}
                    placeholder="e.g., Define Market Segmentation"
                    className="w-full bg-surface-container-low border border-outline-variant/30 px-6 py-4 rounded-2xl focus:ring-2 focus:ring-primary outline-none transition-all font-bold text-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-4">Topic Association *</label>
                    <select
                      required
                      value={newTask.topicId}
                      onChange={e => setNewTask({ ...newTask, topicId: e.target.value })}
                      className="w-full bg-surface-container-low border border-outline-variant/30 px-6 py-4 rounded-2xl focus:ring-2 focus:ring-primary outline-none transition-all font-bold text-sm appearance-none"
                    >
                      <option value="">Select Topic</option>
                      {topics.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase text-on-surface-variant ml-4">Submission Type *</label>
                    <div className="flex bg-surface-container-low border border-outline-variant/30 p-1 rounded-2xl h-[58px]">
                      <button
                        type="button"
                        onClick={() => setNewTask({ ...newTask, type: "TEXT" })}
                        className={`flex-1 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${newTask.type === "TEXT" ? "bg-white shadow-sm text-primary" : "text-slate-400 hover:text-slate-600"}`}
                      >
                        Text
                      </button>
                      <button
                        type="button"
                        onClick={() => setNewTask({ ...newTask, type: "LINK" })}
                        className={`flex-1 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${newTask.type === "LINK" ? "bg-white shadow-sm text-primary" : "text-slate-400 hover:text-slate-600"}`}
                      >
                        Link
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-4">Task Description</label>
                  <textarea
                    value={newTask.description}
                    onChange={e => setNewTask({ ...newTask, description: e.target.value })}
                    placeholder="Describe what needs to be done for this task..."
                    className="w-full bg-surface-container-low border border-outline-variant/30 px-6 py-4 rounded-2xl focus:ring-2 focus:ring-primary outline-none transition-all font-bold text-sm min-h-[100px] resize-none"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-4">Checklist / Rules</label>
                  <div className="flex gap-2">
                    <input
                      value={currentReq}
                      onChange={e => setCurrentReq(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                           if (currentReq.trim()) {
                            setNewTask({ ...newTask, requirements: [...(newTask.requirements || []), currentReq.trim()] });
                            setCurrentReq("");
                          }
                        }
                      }}
                      placeholder="Add a step or rule..."
                      className="flex-1 bg-surface-container-low border border-outline-variant/30 px-6 py-4 rounded-2xl focus:ring-2 focus:ring-primary outline-none transition-all font-bold text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (currentReq.trim()) {
                          setNewTask({ ...newTask, requirements: [...(newTask.requirements || []), currentReq.trim()] });
                          setCurrentReq("");
                        }
                      }}
                      className="px-6 bg-surface-container-high rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-primary hover:text-white transition-all"
                    >
                      Add
                    </button>
                  </div>
                  
                  {newTask.requirements && newTask.requirements.length > 0 && (
                    <div className="mt-4 space-y-2 max-h-[150px] overflow-y-auto pr-2 custom-scrollbar">
                      {newTask.requirements.map((req, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-surface-container-low/50 p-3 rounded-xl border border-outline-variant/10 group">
                          <div className="flex items-center gap-3">
                            <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-black">{idx + 1}</span>
                            <span className="text-xs font-bold text-slate-600">{req}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setNewTask({ ...newTask, requirements: newTask.requirements.filter((_, i) => i !== idx) })}
                            className="text-slate-400 hover:text-error transition-colors"
                          >
                            <span className="material-symbols-outlined text-[16px]">delete</span>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-4">Points for completing this task *</label>
                  <div className="relative">
                    <span className="absolute left-6 top-1/2 -translate-y-1/2 text-primary font-black">◈</span>
                    <input
                      required
                      type="number"
                      min="0"
                      value={newTask.points}
                      onChange={e => setNewTask({ ...newTask, points: parseInt(e.target.value) || 0 })}
                      placeholder="100"
                      className="w-full bg-surface-container-low border border-outline-variant/30 pl-12 pr-6 py-4 rounded-2xl focus:ring-2 focus:ring-primary outline-none transition-all font-bold text-sm"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isCreating}
                  className="w-full py-4 bg-primary text-on-primary rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50"
                >
                  {isCreating ? (editingTaskId ? "Saving..." : "Creating...") : (editingTaskId ? "Save Changes" : "Create Task")}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
      {/* Complete Course Confirmation Dialog */}
      <Dialog 
        isOpen={showCompleteConfirm} 
        onClose={() => setShowCompleteConfirm(false)} 
        title="Mark Course Completed"
        size="sm"
        footer={
          <div className="flex gap-4 justify-end">
            <button 
              onClick={() => setShowCompleteConfirm(false)} 
              className="px-6 py-3.5 bg-surface-container-high text-on-surface rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-surface-container-highest transition-colors"
            >
              Cancel
            </button>
            <button 
              onClick={async () => {
                try {
                  if (!userId) throw new Error("Not logged in");
                  setIsCompleting(true);
                  await api.post(`/courses/${courseId}/complete`, { userId });
                  setCompleted(true);
                  setShowCompleteConfirm(false);
                  if (feedback_links && feedback_links.length > 0) {
                    setShowFeedbackModal(true);
                  }
                } catch (err) {
                  console.error("Failed to mark course as completed", err);
                  alert("Failed to mark course as completed. Please try again.");
                } finally {
                  setIsCompleting(false);
                }
              }} 
              disabled={isCompleting}
              className="px-6 py-3.5 bg-emerald-500 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-emerald-600 transition-colors shadow-lg shadow-emerald-500/20 disabled:opacity-50"
            >
              {isCompleting ? "Submitting..." : "Confirm"}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
            <p className="text-on-surface-variant text-lg font-medium">
              Are you sure?
            </p>
            <div className="flex items-start gap-3 p-4 bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/20 rounded-xl">
              <span className="material-symbols-outlined text-[20px] mt-0.5">check_circle</span>
              <p className="text-sm font-medium leading-relaxed">
                <strong className="font-black">Awesome job!</strong> Claim your XP and keep growing!
              </p>
            </div>
          </div>
      </Dialog>

      {/* Feedback Links Dialog */}
      {feedback_links && feedback_links.length > 0 && (
        <Dialog 
          isOpen={showFeedbackModal} 
          onClose={() => setShowFeedbackModal(false)} 
          title="Share Your Experience"
          size="md"
        >
          <div className="space-y-6 text-center">
            <div className="w-20 h-20 bg-emerald-500/10 text-emerald-500 rounded-full flex items-center justify-center mx-auto mt-4 mb-2">
              <span className="material-symbols-outlined text-4xl">celebration</span>
            </div>
            <h3 className="text-xl font-black text-on-surface">Congratulations on completing this class!</h3>
            <p className="text-sm text-on-surface-variant font-medium">
              We'd love to hear your thoughts. If you enjoyed this content, please consider leaving a review on your favorite platform:
            </p>
            
            <div className="flex flex-col gap-3 mt-6 pb-6">
              {feedback_links.map((url, idx) => {
                const getPlatformInfo = (linkUrl: string) => {
                  const lower = (linkUrl || "").toLowerCase();
                  if (lower.includes('facebook.com') || lower.includes('fb.me')) return { 
                    label: 'Write a feedback on Facebook',
                    themeClasses: 'bg-[#1877F2] text-white hover:bg-[#0c63d4]',
                    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  };
                  if (lower.includes('instagram.com') || lower.includes('instagr.am')) return { 
                    label: 'Write a feedback on Instagram',
                    themeClasses: 'bg-gradient-to-tr from-[#f09433] via-[#dc2743] to-[#bc1888] text-white hover:opacity-90',
                    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                  };
                  if (lower.includes('youtube.com') || lower.includes('youtu.be')) return { 
                    label: 'Write a feedback on YouTube',
                    themeClasses: 'bg-[#FF0000] text-white hover:bg-[#cc0000]',
                    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                  };
                  if (lower.includes('trustpilot.com')) return { 
                    label: 'Write a feedback on Trustpilot',
                    themeClasses: 'bg-[#00B67A] text-white hover:bg-[#009664]',
                    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M12 0l3.708 7.514L24 8.72l-6 5.847L19.416 24 12 20.1 4.584 24 6 14.567 0 8.72l8.292-1.206L12 0z"/></svg>
                  };
                  return { 
                    label: 'Leave a Review',
                    themeClasses: 'bg-surface-container-high text-on-surface hover:bg-surface-container-highest border border-outline-variant/10',
                    icon: <span className="material-symbols-outlined text-[24px]">rate_review</span>
                  };
                };
                const platform = getPlatformInfo(url);
                return (
                  <a 
                    key={idx}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center justify-between px-5 py-4 rounded-2xl transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5 ${platform.themeClasses}`}
                  >
                    <div className="flex items-center gap-4">
                      {platform.icon}
                      <span className="font-bold">{platform.label}</span>
                    </div>
                    <span className="material-symbols-outlined">open_in_new</span>
                  </a>
                );
              })}
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
}
